import React, { useState, useRef, useEffect } from 'react';
import { Menu, Bot, X, Zap, MessageSquarePlus, Trash2 } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, orderBy, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import personalChatService from '../services/personalChatService';
import usageTrackerService from '../services/usageTrackerService';

// Components - Reuse from TeamChat
import MessageItem from '../components/TeamChat/Chat/MessageItem';
import MessageInput from '../components/TeamChat/Chat/MessageInput';
import AIConfigModal from '../components/TeamChat/AI/AIConfigModal';

// Types
import { AIProvider } from '../types/chat.types';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Timestamp;
  conversationId: string;
}

interface Conversation {
  id: string;
  title: string;
  created_at: Timestamp;
  last_message_at: Timestamp;
  message_count: number;
}

interface ChatMessage {
  id: string;
  channel_id: string;
  workspace_id: string;
  user_id: string;
  user_name: string;
  user_avatar?: string;
  content: string;
  type: 'text' | 'ai' | 'system';
  created_at: Timestamp;
  edited_at?: Timestamp;
  is_ai?: boolean;
  ai_model?: string;
}

const PersonalChat: React.FC = () => {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();

  // State
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // AI State
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<AIProvider | null>('openai');
  const [selectedModel, setSelectedModel] = useState<string>('gpt-4o');

  // Usage State
  const [monthlyUsage, setMonthlyUsage] = useState<any>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null!);
  const lastSentMessageRef = useRef<string>('');
  const lastSentTimeRef = useRef<number>(0);

  // Load conversations
  useEffect(() => {
    if (!user || !currentWorkspace) return;

    const loadConversations = async () => {
      try {
        const conversationsRef = collection(db, 'personalChats');
        const q = query(
          conversationsRef,
          where('userId', '==', user.firebase_uid),
          where('workspaceId', '==', currentWorkspace.id),
          orderBy('last_message_at', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          const convList: Conversation[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            convList.push({
              id: doc.id,
              title: data.title || '새 대화',
              created_at: data.created_at,
              last_message_at: data.last_message_at,
              message_count: data.message_count || 0
            });
          });
          setConversations(convList);

          // Select first conversation if none selected
          if (!currentConversation && convList.length > 0) {
            setCurrentConversation(convList[0]);
          }
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Failed to load conversations:', error);
      }
    };

    loadConversations();
  }, [user, currentWorkspace, currentConversation]);

  // Load messages for current conversation
  useEffect(() => {
    if (!currentConversation || !user) {
      setMessages([]);
      return;
    }

    const messagesRef = collection(db, 'personalChats', currentConversation.id, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messageList: ChatMessage[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        messageList.push({
          id: doc.id,
          channel_id: currentConversation.id,
          workspace_id: currentWorkspace?.id || '',
          user_id: data.role === 'user' ? (user.firebase_uid || '') : 'ai-assistant',
          user_name: data.role === 'user' ? (user.name || 'User') : `AI (${selectedModel})`,
          user_avatar: data.role === 'user' ? user.avatar_url : undefined,
          content: data.content,
          type: data.role === 'assistant' ? 'ai' : 'text',
          created_at: data.timestamp,
          is_ai: data.role === 'assistant',
          ai_model: data.role === 'assistant' ? selectedModel : undefined
        });
      });
      setMessages(messageList);
    });

    return () => unsubscribe();
  }, [currentConversation, user, currentWorkspace, selectedModel]);

  // Load usage data
  useEffect(() => {
    const loadUsage = async () => {
      if (currentWorkspace) {
        try {
          const usage = await usageTrackerService.getWorkspaceMonthlyUsage(currentWorkspace.id);
          setMonthlyUsage(usage);
        } catch (error) {
          console.error('Failed to load usage data:', error);
        }
      }
    };

    loadUsage();
    const interval = setInterval(loadUsage, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentWorkspace]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Create new conversation
  const createNewConversation = async () => {
    if (!user || !currentWorkspace) return;

    try {
      const conversationsRef = collection(db, 'personalChats');
      const docRef = await addDoc(conversationsRef, {
        userId: user.firebase_uid,
        workspaceId: currentWorkspace.id,
        title: '새 대화',
        created_at: serverTimestamp(),
        last_message_at: serverTimestamp(),
        message_count: 0
      });

      const newConv: Conversation = {
        id: docRef.id,
        title: '새 대화',
        created_at: Timestamp.now(),
        last_message_at: Timestamp.now(),
        message_count: 0
      };

      setCurrentConversation(newConv);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  // Delete conversation
  const deleteConversation = async (convId: string) => {
    if (!user || !currentWorkspace) return;

    try {
      // Delete all messages first
      const messagesRef = collection(db, 'personalChats', convId, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);
      const deletePromises = messagesSnapshot.docs.map(docSnap => deleteDoc(docSnap.ref));
      await Promise.all(deletePromises);

      // Delete conversation
      await deleteDoc(doc(db, 'personalChats', convId));

      if (currentConversation?.id === convId) {
        const remainingConvs = conversations.filter(c => c.id !== convId);
        setCurrentConversation(remainingConvs[0] || null);
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user || !currentWorkspace || !selectedProvider || !selectedModel) {
      if (!selectedProvider || !selectedModel) {
        setShowAIModal(true);
        return;
      }
      return;
    }

    // Prevent duplicate sends
    if (isSending) {
      return;
    }

    const now = Date.now();
    const messageToSend = newMessage.trim();
    if (lastSentMessageRef.current === messageToSend && now - lastSentTimeRef.current < 500) {
      setNewMessage('');
      return;
    }

    setIsSending(true);
    lastSentMessageRef.current = messageToSend;
    lastSentTimeRef.current = now;

    try {
      let conversationToUse = currentConversation;

      // Create conversation if none exists
      if (!conversationToUse) {
        const conversationsRef = collection(db, 'personalChats');
        const docRef = await addDoc(conversationsRef, {
          userId: user.firebase_uid,
          workspaceId: currentWorkspace.id,
          title: messageToSend.substring(0, 50),
          created_at: serverTimestamp(),
          last_message_at: serverTimestamp(),
          message_count: 0
        });

        conversationToUse = {
          id: docRef.id,
          title: messageToSend.substring(0, 50),
          created_at: Timestamp.now(),
          last_message_at: Timestamp.now(),
          message_count: 0
        };

        setCurrentConversation(conversationToUse);
      }

      // Save user message
      const messagesRef = collection(db, 'personalChats', conversationToUse.id, 'messages');
      await addDoc(messagesRef, {
        role: 'user',
        content: messageToSend,
        timestamp: serverTimestamp(),
        conversationId: conversationToUse.id
      });

      setNewMessage('');
      setIsLoading(true);

      // Get AI response using personal chat service (separate from team chat)
      const systemPrompt = `You are a helpful AI assistant for AXTIV workspace.

Guidelines for responses:
- Provide detailed, comprehensive answers with depth and context
- Include relevant examples, explanations, and background information when appropriate
- Break down complex topics into understandable sections
- Use structured formatting (lists, headings) for better readability
- When asked about recent events or information, use web search to provide up-to-date answers
- Cite sources when using web search results
- Be thorough but maintain clarity and professional tone`;
      const fullResponse = await personalChatService.streamChat(
        messageToSend,
        selectedProvider,
        systemPrompt,
        conversationToUse.id
      );

      // Save AI response
      await addDoc(messagesRef, {
        role: 'assistant',
        content: fullResponse,
        timestamp: serverTimestamp(),
        conversationId: conversationToUse.id
      });

      // Track Pulse usage
      const tokenCount = Math.ceil((messageToSend.length + fullResponse.length) / 4);
      const pulseCost = Math.ceil(tokenCount / 1000);

      await addDoc(collection(db, 'api_usage_records'), {
        user_id: user.firebase_uid,
        workspace_id: currentWorkspace.id,
        company_id: currentWorkspace.companyId || '',
        feature: 'personal_chat',
        model: selectedModel,
        input_tokens: Math.ceil(messageToSend.length / 4),
        output_tokens: Math.ceil(fullResponse.length / 4),
        total_tokens: tokenCount,
        pulse_used: pulseCost,
        timestamp: serverTimestamp()
      });

      // Update conversation
      const convRef = doc(db, 'personalChats', conversationToUse.id);
      await addDoc(collection(convRef, 'messages'), {
        last_message_at: serverTimestamp(),
        message_count: (conversationToUse.message_count || 0) + 2,
        title: conversationToUse.message_count === 0 ? messageToSend.substring(0, 50) : conversationToUse.title
      });

    } catch (error) {
      console.error('Failed to send message:', error);
      alert('메시지 전송에 실패했습니다: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setIsSending(false);
      }, 100);
    }
  };

  // Share to team chat
  const shareToTeamChat = async (message: ChatMessage) => {
    if (!currentWorkspace || !user) return;

    try {
      const channelsRef = collection(db, 'workspaces', currentWorkspace.id, 'channels');
      const q = query(channelsRef, where('name', '==', 'general'));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        alert('일반 채널을 찾을 수 없습니다.');
        return;
      }

      const generalChannel = snapshot.docs[0];
      const messagesRef = collection(db, 'workspaces', currentWorkspace.id, 'channels', generalChannel.id, 'messages');

      await addDoc(messagesRef, {
        content: `💡 **개인 AI 채팅 공유**\n\n${message.content}`,
        user_id: user.firebase_uid,
        user_name: user.name,
        user_avatar: user.avatar_url || '',
        timestamp: serverTimestamp(),
        type: 'text',
        metadata: {
          sharedFrom: 'personal_chat',
          originalTimestamp: message.created_at
        }
      });

      alert('팀 채팅에 공유되었습니다!');
    } catch (error) {
      console.error('Failed to share to team chat:', error);
      alert('공유에 실패했습니다.');
    }
  };

  // Configure AI
  const handleAIConfirm = () => {
    if (selectedProvider && selectedModel) {
      setShowAIModal(false);
    }
  };

  return (
    <div className="h-[calc(100vh-64px)] flex bg-white overflow-hidden">
      {/* Sidebar */}
      <div className={`${showMobileSidebar ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:relative w-64 h-full bg-slate-800 text-white transition-transform duration-300 z-50 flex flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold">개인 AI 채팅</span>
            </div>
            <button
              onClick={() => setShowMobileSidebar(false)}
              className="md:hidden p-1 hover:bg-slate-700 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* New Conversation Button */}
        <div className="p-4">
          <button
            onClick={createNewConversation}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <MessageSquarePlus className="w-4 h-4" />
            새 대화
          </button>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-2">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg mb-1 cursor-pointer transition-colors ${
                  currentConversation?.id === conv.id
                    ? 'bg-slate-700'
                    : 'hover:bg-slate-700/50'
                }`}
                onClick={() => setCurrentConversation(conv)}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{conv.title}</div>
                  <div className="text-xs text-slate-400">
                    {conv.message_count || 0} 메시지
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteConversation(conv.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-600 rounded transition-opacity"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowMobileSidebar(true)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-600" />
              <div className="flex flex-col">
                <h2 className="font-semibold text-gray-900">
                  {currentConversation ? currentConversation.title : '대화를 선택하세요'}
                </h2>
                {selectedProvider && selectedModel && (
                  <span className="text-xs text-gray-500">
                    {selectedProvider === 'openai' ? 'OpenAI' : 'Anthropic'} · {selectedModel}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAIModal(true)}
              className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
            >
              <Bot className="w-4 h-4" />
              AI 설정
            </button>
            {monthlyUsage && (
              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg">
                  <Zap className="w-4 h-4" />
                  <span className="font-medium">사용: {Math.floor(monthlyUsage.totalCostPulse || 0)} Pulse</span>
                </div>
                <div className="flex items-center gap-1 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg">
                  <Zap className="w-4 h-4" />
                  <span className="font-medium">잔액: {Math.floor(monthlyUsage.remainingPulse || 10000)} Pulse</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0">
          {!currentConversation ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <Bot className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg mb-2">개인 AI 채팅에 오신 것을 환영합니다</p>
                <p className="text-sm">새 대화를 시작하여 AI와 대화하세요</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <p className="text-lg mb-2">대화를 시작하세요</p>
                <p className="text-sm">첫 번째 메시지를 보내보세요!</p>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <MessageItem
                key={message.id}
                message={message}
                currentUserId={user?.firebase_uid || ''}
                isAdmin={false}
                onEdit={() => {}}
                onDelete={() => {}}
                onMenuToggle={() => {}}
                showMenu={false}
              />
            ))
          )}
          {isLoading && (
            <div className="flex items-center gap-2 text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm">AI가 응답하는 중...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="border-t">
          <MessageInput
            value={newMessage}
            onChange={setNewMessage}
            onSend={handleSendMessage}
            onFileSelect={() => {}}
            onFileRemove={() => {}}
            onAIInvite={() => setShowAIModal(true)}
            uploadedFiles={[]}
            filePreview={[]}
            isUploading={false}
            placeholder={currentConversation ? 'AI에게 메시지 보내기...' : '대화를 시작하려면 새 대화를 만드세요'}
            inputRef={inputRef}
            onKeyDown={() => {}}
          />
        </div>
      </div>

      {/* AI Config Modal */}
      <AIConfigModal
        show={showAIModal}
        selectedProvider={selectedProvider}
        selectedModel={selectedModel}
        onProviderChange={setSelectedProvider}
        onModelChange={setSelectedModel}
        onClose={() => setShowAIModal(false)}
        onConfirm={handleAIConfirm}
      />
    </div>
  );
};

export default PersonalChat;
